import React, { useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';

/**
 * DesignRenderer — sandboxed iframe that compiles and renders JSX strings.
 *
 * Phase 3 additions:
 * - Injects data-design-id="el-N" into every JSX opening tag before render
 * - In edit mode: hover outline overlay, click → ELEMENT_SELECTED postMessage
 * - Accepts PREVIEW_STYLE messages to apply live CSS without re-render
 * - Exposes previewStyle(elementId, styles) via ref for parent to call
 *
 * Security note: Function(transpiled)() is intentional — runs in a strict
 * sandbox="allow-scripts" iframe with no same-origin access.
 */

// ── Client-side ID injection ──────────────────────────────────────────────────

const OPEN_TAG_RE = /<([A-Za-z][A-Za-z0-9.]*)(?=[\s>\/])/g;

function injectDesignIds(content) {
  let counter = 0;
  return content.replace(OPEN_TAG_RE, (m) => {
    counter++;
    return `${m} data-design-id="el-${counter}"`;
  });
}

// ── Iframe HTML ───────────────────────────────────────────────────────────────

const IFRAME_HTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; }
    #root { height: 100%; }
    #error-overlay {
      position: fixed; inset: 0; background: rgba(255,240,240,0.97);
      padding: 24px; font-family: monospace; font-size: 13px;
      color: #c0392b; overflow: auto; z-index: 9999;
      white-space: pre-wrap; display: none;
    }
    #error-overlay.visible { display: block; }
    #hover-ring {
      position: fixed; pointer-events: none;
      border: 2px solid #6366f1; border-radius: 2px;
      display: none; z-index: 99998; box-sizing: border-box;
    }
    #select-ring {
      position: fixed; pointer-events: none;
      border: 2px solid #e85d26; border-radius: 2px;
      display: none; z-index: 99999; box-sizing: border-box;
    }
  </style>
</head>
<body>
  <div id="root"></div>
  <div id="error-overlay"></div>
  <div id="hover-ring"></div>
  <div id="select-ring"></div>
  <script>
    var errorEl = document.getElementById('error-overlay');
    var hoverRing = document.getElementById('hover-ring');
    var selectRing = document.getElementById('select-ring');
    var editMode = false;
    var commentMode = false;
    var selectedEl = null;
    var hoveredEl = null;

    function showError(msg) {
      errorEl.textContent = 'Render Error\\n\\n' + msg;
      errorEl.classList.add('visible');
    }
    function hideError() {
      errorEl.classList.remove('visible');
      errorEl.textContent = '';
    }

    function positionRing(ring, el) {
      if (!el) { ring.style.display = 'none'; return; }
      var r = el.getBoundingClientRect();
      ring.style.top = r.top + 'px';
      ring.style.left = r.left + 'px';
      ring.style.width = r.width + 'px';
      ring.style.height = r.height + 'px';
      ring.style.display = 'block';
    }

    function extractStyles(cs) {
      var props = [
        'color','backgroundColor','fontSize','fontFamily','fontWeight',
        'lineHeight','letterSpacing','textAlign','fontStyle',
        'paddingTop','paddingRight','paddingBottom','paddingLeft',
        'marginTop','marginRight','marginBottom','marginLeft',
        'width','height','minWidth','maxWidth','minHeight','maxHeight',
        'display','flexDirection','justifyContent','alignItems','gap','flexWrap',
        'border','borderRadius','borderColor','borderWidth',
        'borderTopLeftRadius','borderTopRightRadius','borderBottomLeftRadius','borderBottomRightRadius',
        'opacity','boxShadow','overflow','position','top','right','bottom','left',
      ];
      var result = {};
      props.forEach(function(p) {
        var cssName = p.replace(/([A-Z])/g, function(c) { return '-' + c.toLowerCase(); });
        result[p] = cs.getPropertyValue(cssName) || cs[p] || '';
      });
      return result;
    }

    document.addEventListener('mousemove', function(e) {
      if (!editMode) return;
      var target = e.target.closest('[data-design-id]');
      if (target === hoveredEl) return;
      hoveredEl = target;
      if (target && target !== selectedEl) {
        positionRing(hoverRing, target);
      } else {
        hoverRing.style.display = 'none';
      }
    });

    document.addEventListener('mouseleave', function() {
      hoverRing.style.display = 'none';
      hoveredEl = null;
    });

    document.addEventListener('click', function(e) {
      if (commentMode) {
        e.preventDefault();
        e.stopPropagation();
        window.parent.postMessage({
          type: 'CANVAS_CLICK',
          x: e.clientX / window.innerWidth,
          y: e.clientY / window.innerHeight,
        }, '*');
        return;
      }
      if (!editMode) return;
      e.preventDefault();
      e.stopPropagation();

      var target = e.target.closest('[data-design-id]');
      selectedEl = target;
      positionRing(selectRing, target);
      hoverRing.style.display = 'none';

      if (target) {
        var cs = window.getComputedStyle(target);
        var rect = target.getBoundingClientRect();
        window.parent.postMessage({
          type: 'ELEMENT_SELECTED',
          elementId: target.getAttribute('data-design-id'),
          tagName: target.tagName.toLowerCase(),
          styles: extractStyles(cs),
          rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
        }, '*');
      } else {
        window.parent.postMessage({ type: 'ELEMENT_DESELECTED' }, '*');
      }
    });

    window.addEventListener('scroll', function() {
      if (selectedEl) positionRing(selectRing, selectedEl);
    }, true);

    window.addEventListener('resize', function() {
      if (selectedEl) positionRing(selectRing, selectedEl);
    });

    window.addEventListener('message', function(e) {
      var data = e.data || {};

      if (data.type === 'SET_INTERACTION_MODE') {
        // mode: 'edit' | 'comment' | 'none'
        editMode = (data.mode === 'edit');
        commentMode = (data.mode === 'comment');
        if (!editMode) {
          hoverRing.style.display = 'none';
          selectRing.style.display = 'none';
          selectedEl = null;
          hoveredEl = null;
        }
        return;
      }

      if (data.type === 'SET_EDIT_MODE') {
        editMode = !!data.enabled;
        if (!editMode) {
          hoverRing.style.display = 'none';
          selectRing.style.display = 'none';
          selectedEl = null;
          hoveredEl = null;
        }
        return;
      }

      if (data.type === 'PREVIEW_STYLE') {
        var previewEl = document.querySelector('[data-design-id="' + data.elementId + '"]');
        if (previewEl && data.styles) {
          Object.assign(previewEl.style, data.styles);
          positionRing(selectRing, previewEl);
        }
        return;
      }

      if (data.type === 'DESELECT') {
        selectedEl = null;
        selectRing.style.display = 'none';
        return;
      }

      if (data.type !== 'RENDER_FILES' || !Array.isArray(data.files)) return;

      hideError();
      selectedEl = null;
      hoverRing.style.display = 'none';
      selectRing.style.display = 'none';

      var root = document.getElementById('root');
      // Unmount React tree first so its fiber bookkeeping is clean,
      // then wipe the DOM — prevents "removeChild: not a child" errors
      // when the same root is reused across successive renders.
      if (window.ReactDOM && root._reactRootContainer) {
        window.ReactDOM.unmountComponentAtNode(root);
      }
      while (root.firstChild) root.removeChild(root.firstChild);

      try {
        data.files.forEach(function(file) {
          var transpiled = Babel.transform(file.content, {
            presets: ['react'],
            filename: file.name,
          }).code;
          // Sandbox eval — intentional, runs inside allow-scripts iframe only
          // nosemgrep: javascript.browser.security.eval-detected
          Function(transpiled)(); /* jshint ignore:line */
        });
      } catch (err) {
        showError(err.message + '\\n\\n' + (err.stack || ''));
      }
    });

    window.addEventListener('error', function(e) {
      showError(e.message + ' (line ' + e.lineno + ')');
    });
  </script>
</body>
</html>`;

let blobUrl;
try {
  blobUrl = URL.createObjectURL(
    new Blob([IFRAME_HTML], { type: 'text/html' })
  );
} catch {
  blobUrl = 'about:blank';
}
const IFRAME_BLOB_URL = blobUrl;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Props:
 *   files            Array<{ name, content }> — ordered (screens.jsx first)
 *   width / height   Iframe dimensions
 *   editMode         boolean — enables click selection & hover outlines
 *   onElementSelected(info) — { elementId, tagName, styles, rect }
 *   onElementDeselected()
 *   style            Extra iframe style
 *
 * Ref methods (via forwardRef):
 *   previewStyle(elementId, styles) — live preview CSS in iframe
 *   deselect()                      — programmatic deselect
 */
const DesignRenderer = forwardRef(({
  files,
  width = '100%',
  height = '100%',
  editMode = false,
  commentMode = false,
  onElementSelected,
  onElementDeselected,
  onCanvasClick,
  style,
}, ref) => {
  const iframeRef = useRef(null);
  const readyRef = useRef(false);
  const pendingRef = useRef(null);
  const editModeRef = useRef(editMode);
  const commentModeRef = useRef(commentMode);

  useImperativeHandle(ref, () => ({
    previewStyle: (elementId, styles) => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'PREVIEW_STYLE', elementId, styles }, '*'
      );
    },
    deselect: () => {
      iframeRef.current?.contentWindow?.postMessage({ type: 'DESELECT' }, '*');
    },
  }));

  const sendFiles = useCallback((filesToRender) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    const withIds = filesToRender.map((f) => ({
      ...f,
      content: injectDesignIds(f.content || ''),
    }));
    iframe.contentWindow.postMessage({ type: 'RENDER_FILES', files: withIds }, '*');
  }, []);

  const sendInteractionMode = useCallback((isEdit, isComment) => {
    const mode = isComment ? 'comment' : isEdit ? 'edit' : 'none';
    iframeRef.current?.contentWindow?.postMessage({ type: 'SET_INTERACTION_MODE', mode }, '*');
  }, []);

  useEffect(() => {
    editModeRef.current = editMode;
    commentModeRef.current = commentMode;
    if (readyRef.current) sendInteractionMode(editMode, commentMode);
  }, [editMode, commentMode, sendInteractionMode]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const onLoad = () => {
      readyRef.current = true;
      sendInteractionMode(editModeRef.current, commentModeRef.current);
      if (pendingRef.current) {
        sendFiles(pendingRef.current);
        pendingRef.current = null;
      }
    };

    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [sendFiles, sendInteractionMode]);

  useEffect(() => {
    if (!files || files.length === 0) return;
    if (readyRef.current) {
      sendFiles(files);
    } else {
      pendingRef.current = files;
    }
  }, [files, sendFiles]);

  useEffect(() => {
    const handler = (e) => {
      const data = e.data || {};
      if (data.type === 'ELEMENT_SELECTED') {
        onElementSelected?.(data);
      } else if (data.type === 'ELEMENT_DESELECTED') {
        onElementDeselected?.();
      } else if (data.type === 'CANVAS_CLICK') {
        onCanvasClick?.(data.x, data.y);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onElementSelected, onElementDeselected, onCanvasClick]);

  return (
    <iframe
      ref={iframeRef}
      src={IFRAME_BLOB_URL}
      title="design-renderer"
      sandbox="allow-scripts"
      style={{
        width,
        height,
        border: 'none',
        display: 'block',
        background: 'transparent',
        cursor: editMode ? 'crosshair' : commentMode ? 'cell' : 'default',
        ...style,
      }}
    />
  );
});

DesignRenderer.displayName = 'DesignRenderer';
export default DesignRenderer;
